import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import {
  Box,
  Collapse,
  Dialog,
  Paper,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { ArrowNarrowLeft, ChevronDown, RefreshCw01, XClose } from "@untitled-ui/icons-react"
import { enqueueSnackbar } from "notistack"
import { useContext, useEffect, useMemo, useState } from "react"

import { Link, Navigate, useSearchParams } from "react-router-dom"

import { useAo } from "../ao/ao"
import { burn, OrderStatus, readBurnResult, useReserves } from "../ao/pool"
import { getPoolMap } from "../ao/router"
import { getBalance, TokenInfo, useTotalSupply } from "../ao/token"
import Alert from "../components/Alert"
import Button from "../components/Button"
import { CardholderIcon } from "../components/CardholderIcon"
import { IconButton } from "../components/IconButton"
import { Subheading } from "../components/Subheading"
import TokenInput from "../components/TokenInput"
import TokenLogo from "../components/TokenLogo"
import TokenPill from "../components/TokenPill"
import { RemoveOrderDetails } from "../components/remove/RemoveOrderDetails"
import { RemoveOrderPendingForm } from "../components/remove/RemoveOrderPendingForm"
import { RemoveOrderPreviewForm } from "../components/remove/RemoveOrderPreviewForm"
import { RemoveOrderSnackbar } from "../components/remove/RemoveOrderSnackbar"
import { RemoveOrderSuccessForm } from "../components/remove/RemoveOrderSuccessForm"
import { ConfigContext } from "../contexts/config"
import { useTokenInfo } from "../hooks/use-token-info"
import { REFRESH_INTERVAL } from "../settings"
import { NumberFont } from "../theme"
import {
  formatNumberAuto,
  formatTicker,
  parseBigIntAsNumber,
  parseNumberAsBigInt,
} from "../utils/format"
import { retryable } from "../utils/utils"

export default function Remove() {
  useEffect(() => {
    document.title = "Withdraw Liquidity · Botega"
  }, [])

  const [searchParams] = useSearchParams()
  const poolId = searchParams.get("pool") || ""

  // inputs
  const [lpAmount, setLpAmount] = useState<string>("")

  // pools
  const { data: poolMap } = useQuery({
    queryKey: ["poolMap"],
    queryFn: () => getPoolMap(ao),
  })

  const pool = useMemo(() => (!poolMap ? undefined : poolMap[poolId]), [poolMap, poolId])
  const tokenAId = pool ? pool[0] : ""
  const tokenBId = pool ? pool[1] : ""

  // token infos
  const [tokenA] = useTokenInfo(tokenAId)
  const [tokenB] = useTokenInfo(tokenBId)

  // reserves
  const [reserves, loadingReserves, refetchReserves] = useReserves(poolId, tokenAId, tokenBId)

  const [lpToken] = useTokenInfo(poolId)

  // ao instance
  const ao = useAo()

  // active address
  const activeAddr = useActiveAddress()

  // config
  const [config] = useContext(ConfigContext)

  // connect wallet
  const { connect } = useConnection()

  // normalized input values
  const lpAmountNormalized = useMemo<bigint>(() => {
    if (!lpToken) return 0n
    try {
      return parseNumberAsBigInt(lpAmount, lpToken.denomination)
    } catch {
      return 0n
    }
  }, [lpAmount, lpToken])

  // pool token total supply
  const [lpTotalSupply, loadingTotalSupply, refetchLpTotalSupply] = useTotalSupply(poolId)

  // balances
  const {
    data: lpBalance,
    refetch: refetchLpBalance,
    isLoading: loadingLpBalance,
    error: lpBalanceError,
  } = useQuery({
    queryKey: [`balance_${poolId}`, activeAddr, config],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr) throw new Error("No active address")

      try {
        const balance = await getBalance(poolId, activeAddr, ao)
        console.log(`Successfully fetched balance for ${poolId}: ${balance.toString()}`)
        return balance
      } catch (error) {
        // For 429 errors, we need to throw them to trigger React Query's retry mechanism
        const err = error as any
        if (
          err.status === 429 ||
          err.statusCode === 429 ||
          (error instanceof Error && error.message.includes("429"))
        ) {
          console.log("Encountered 429 error, triggering retry:", error)
          throw error // Re-throw to trigger retry
        }

        // For other errors, log but return a default value
        console.error("Error fetching LP balance:", error)
        return 0n
      }
    },
    retry: (failureCount, error) => {
      // Specifically check for 429 errors
      if (error instanceof Error) {
        const err = error as any
        const is429 =
          err.status === 429 ||
          err.statusCode === 429 ||
          error.message.includes("429") ||
          error.message.includes("Too Many Requests")

        if (is429) {
          console.log(`Will retry after 429 error (attempt ${failureCount})`)
          return true
        }
      }
      // For other errors, retry up to 3 times
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => {
      // Even faster retry for 429 errors
      const baseDelay = 300 // Start at 300ms
      const delay = Math.min(baseDelay * 1.3 ** attemptIndex, 3000)
      const jitter = delay * 0.1 * Math.random()
      console.log(
        `LP balance retry delay: ${Math.round(delay + jitter)}ms (attempt ${attemptIndex + 1})`,
      )
      return delay + jitter
    },
    // Increase staleTime to reduce refetch frequency
    staleTime: 30 * 1000, // 30 seconds
    // If fetch fails, consider data valid for 2 minutes
    gcTime: 2 * 60 * 1000,
  })

  // details dropdown
  const [detailsDropdownOpen, setDetailsDropdownOpen] = useState(false)

  // prices
  const expectedTokenAFull = useMemo(() => {
    if (!lpTotalSupply || !lpBalance || !tokenAId || !reserves) return 0n
    const value = (lpBalance * reserves[tokenAId]) / lpTotalSupply

    return value
  }, [lpTotalSupply, lpBalance, tokenAId, reserves])
  const expectedTokenBFull = useMemo(() => {
    if (!lpTotalSupply || !lpBalance || !tokenBId || !reserves) return 0n
    const value = (lpBalance * reserves[tokenBId]) / lpTotalSupply

    return value
  }, [lpTotalSupply, lpBalance, tokenBId, reserves])

  const expectedTokenA = useMemo(() => {
    if (!lpTotalSupply || !lpAmountNormalized || !tokenAId || !reserves?.[tokenAId]) return 0n
    const value = (lpAmountNormalized * reserves[tokenAId]) / lpTotalSupply

    console.log("onExpectedOutput", {
      lpAmountNormalized,
      lpTotalSupply,
      expectedTokenA: value,
      reserveA: reserves[tokenAId],
    })

    return value
  }, [lpTotalSupply, lpAmountNormalized, tokenAId, reserves])
  const expectedTokenB = useMemo(() => {
    if (!lpTotalSupply || !lpAmountNormalized || !tokenBId || !reserves?.[tokenBId]) return 0n
    const value = (lpAmountNormalized * reserves[tokenBId]) / lpTotalSupply

    console.log("onExpectedOutput", {
      lpAmountNormalized,
      lpTotalSupply,
      expectedTokenB: value,
      reserveB: reserves[tokenBId],
    })

    return value
  }, [lpTotalSupply, lpAmountNormalized, tokenBId, reserves])

  // pool share
  const currentPoolShare = useMemo(() => {
    if (
      loadingTotalSupply ||
      loadingLpBalance ||
      lpBalance === undefined ||
      lpTotalSupply === undefined
    )
      return null
    if (lpBalance === 0n) return 0n
    if (lpTotalSupply === 0n) return 0n

    const value = (lpBalance * 10000n) / lpTotalSupply
    console.log("onCurrentPoolShareUpdate", {
      lpTotalSupply,
      lpBalance,
      value,
    })

    return value
  }, [loadingTotalSupply, loadingLpBalance, lpBalance, lpTotalSupply])

  const newPoolShare = useMemo(() => {
    if (!loadingTotalSupply && !lpTotalSupply) return 10000n
    if (loadingTotalSupply || lpAmountNormalized === 0n) return null

    if (lpTotalSupply - lpAmountNormalized === 0n) return 0n

    const value =
      (((lpBalance || 0n) - lpAmountNormalized) * 10000n) / (lpTotalSupply - lpAmountNormalized)

    console.log("onNewPoolShareUpdate", {
      lpBalance,
      lpAmountNormalized,
      lpTotalSupply,
      value,
    })

    return value
  }, [loadingTotalSupply, lpTotalSupply, lpAmountNormalized, lpBalance])

  // button validation
  const validationError = useMemo(() => {
    if (!lpAmountNormalized) {
      return "Enter an amount"
    }
    if (typeof lpBalance === "bigint" && lpAmountNormalized > lpBalance) {
      return "Insufficient funds"
    }
    if (!poolId) {
      return "Select a pool"
    }
    if (
      !reserves ||
      typeof reserves[tokenAId] === "undefined" ||
      typeof reserves[tokenBId] === "undefined"
    ) {
      return "Fetching reserves"
    }
    if (!tokenA || !tokenB) {
      return "Fetching token infos"
    }

    return ""
  }, [lpAmountNormalized, lpBalance, poolId, reserves, tokenA, tokenAId, tokenB, tokenBId])

  // confirm popover
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  function showPopover() {
    if (validationError) return
    setConfirmDialogOpen(true)
  }

  function closePopover() {
    if (orderStatus === "success" || orderStatus === "error" || orderStatus === "pending") {
      resetForm()
      enqueueSnackbar(
        <RemoveOrderSnackbar
          lpToken={lpToken as TokenInfo}
          lpAmountNormalized={lpAmountNormalized}
          lpAmount={lpAmount}
          expectedTokenA={orderExpectedTokenA}
          expectedTokenB={orderExpectedTokenB}
          tokenA={tokenA as TokenInfo}
          tokenB={tokenB as TokenInfo}
          poolId={poolId as string}
          orderId={orderId as string}
          orderStatus={orderStatus}
          orderError={orderError}
          tokenAOutput={tokenAOutput}
          tokenBOutput={tokenBOutput}
        />,
        {
          persist: true,
          key: orderId,
        },
      )
    }

    resetState()
    setConfirmDialogOpen(false)
  }

  const [orderExpectedTokenA, setOrderExpectedTokenA] = useState<bigint>(0n)
  const [orderExpectedTokenB, setOrderExpectedTokenB] = useState<bigint>(0n)

  // order id
  const [orderId, setOrderId] = useState<string>()

  // order status
  const [orderStatus, setOrderStatus] = useState<OrderStatus>()
  const [orderConfirmationId, setOrderConfirmationId] = useState<string>()

  // order error
  const [orderError, setOrderError] = useState<string>()

  // burn result
  const [tokenAOutput, setTokenAOutput] = useState<bigint>()
  const [tokenBOutput, setTokenBOutput] = useState<bigint>()

  // execute burn
  async function doBurn() {
    if (!!validationError) return

    try {
      setOrderStatus("pendingWalletConfirmation")
      const orderId = await burn(lpAmountNormalized, poolId, ao)
      console.log("onOrderId", orderId)
      setOrderId(orderId)
      setOrderExpectedTokenA(expectedTokenA)
      setOrderExpectedTokenB(expectedTokenB)
      setOrderStatus("pending")
      await Promise.all([refetchLpBalance(), refetchLpTotalSupply()])
    } catch (error) {
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (error.message.includes("User rejected")) {
        setOrderStatus(undefined)
        return
      }

      setOrderStatus("error")
      setOrderError(error.message)
    }
  }

  useEffect(() => {
    if (!orderId) return

    const abortController = new AbortController()
    ;(async function () {
      try {
        const [withdrawals, confirmationId] = await retryable(readBurnResult)(orderId)

        if (abortController.signal.aborted) return

        setTokenAOutput(withdrawals[tokenAId])
        setTokenBOutput(withdrawals[tokenBId])
        setOrderConfirmationId(confirmationId)

        setOrderStatus("success")
      } catch (error) {
        if (abortController.signal.aborted) return

        if (!(error instanceof Error)) {
          setOrderError(String(error))
          setOrderStatus("error")
          return
        }

        setOrderStatus("error")
        setOrderError(error.message)
      }
    })()

    return () => {
      abortController.abort()
    }
  }, [orderId, tokenAId, tokenBId])

  // reset inputs
  function resetForm() {
    setLpAmount("")
    setTokenAOutput(undefined)
    setTokenBOutput(undefined)
  }

  // reset order state
  function resetState() {
    setOrderStatus(undefined)
    setOrderError(undefined)
    setOrderId(undefined)
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  if (!poolId) {
    return <Navigate to="/pools" />
  }

  return (
    <Page style={{ position: "relative" }}>
      <Toolbar
        className={css`
          background: var(--mui-palette-titleBar);
          border-bottom: 1px solid var(--mui-palette-titleBarBorder);
          height: 34px;

          border-top-left-radius: var(--mui-shape-borderRadius);
          border-top-right-radius: var(--mui-shape-borderRadius);
        `}
      >
        <Link to="/pools">
          <IconButton size="medium">
            <ArrowNarrowLeft />
          </IconButton>
        </Link>
        <Subheading>Withdraw Liquidity</Subheading>
        <Stack direction="row" gap={0.5} alignItems="center">
          {poolId && (
            <IconButton
              size="medium"
              disabled={loadingReserves}
              className={css`
                svg {
                  width: 16px;
                  height: 16px;
                }
              `}
              onClick={() => {
                if (!poolId) return

                refetchReserves().then(({ data }) => {
                  console.log("onReservesUpdate", data)
                })
              }}
            >
              {" "}
              <RefreshCw01
                style={{
                  animation: loadingReserves ? "loading-rotate 0.57s ease-in-out infinite" : "none",
                }}
              />
            </IconButton>
          )}
        </Stack>
      </Toolbar>
      <Stack
        gap={1}
        component={Paper}
        elevation={0}
        sx={{
          padding: 1,
          background: "var(--mui-palette-background-paper)",
          borderTopLeftRadius: "0",
          borderTopRightRadius: "0",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Inputs>
          <Box
            sx={{
              marginTop: 1,
              marginX: 1,
              height: 25,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 0.5,
                display: "flex",
                justifyContent: "center",
                padding: 0.25,
              }}
            >
              <Typography variant="caption">Withdraw</Typography>
            </Paper>
          </Box>
          <Box
            sx={{
              paddingX: 1.25,
              marginTop: 1,
              "& > div": {
                padding: 1,
                paddingX: 2,
              },
            }}
          >
            <TokenPill variant="outlined">
              <TokenLogo tx={tokenA?.logo} alt={`${tokenA?.name} logo`} size="small" />
              {formatTicker(tokenA?.ticker)}{" "}
              <Typography fontWeight={700} variant="subtitle1">
                /
              </Typography>
              <TokenLogo tx={tokenB?.logo} alt={`${tokenB?.name} logo`} size="small" />
              {formatTicker(tokenB?.ticker)}
            </TokenPill>
          </Box>
          <TokenInput
            disableTokenSelect
            showSlider
            tokenID={poolId}
            tokenInfo={lpToken}
            onTokenIdChange={() => {}}
            tokens={null}
            autoFocus
            onSubmit={showPopover}
            value={lpAmount}
            onChange={(newValue, useMax) => {
              console.log("onInputChange", {
                tokenId: tokenAId,
                amount: newValue,
                useMax,
              })
              setLpAmount(newValue)
            }}
            balance={lpBalance}
          />
        </Inputs>
        <Box
          sx={(theme) => ({
            borderRadius: 1,
            paddingY: 1,
            paddingX: 1.5,
            background: "var(--mui-palette-background-default)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            [theme.breakpoints.down("md")]: {
              display: "flex",
              flexDirection: "column",
            },
          })}
        >
          {tokenA && tokenB && !loadingReserves ? (
            <>
              <Stack>
                <Typography fontFamily={NumberFont} color="accent.main">
                  {formatNumberAuto(parseBigIntAsNumber(expectedTokenAFull, tokenA.denomination))}
                </Typography>
                <span>{formatTicker(tokenA.ticker, true)}</span>
              </Stack>
              <Stack>
                <Typography fontFamily={NumberFont} color="accent.main">
                  {formatNumberAuto(parseBigIntAsNumber(expectedTokenBFull, tokenB.denomination))}
                </Typography>
                <span>{formatTicker(tokenB.ticker, true)}</span>
              </Stack>
              <Stack
                sx={{
                  visibility: poolId ? "visible" : "hidden",
                }}
              >
                {currentPoolShare === null ? (
                  <Skeleton width="60px" height="24px" />
                ) : (
                  <Typography fontFamily={NumberFont} color="accent.main">
                    {(Number(currentPoolShare) / 100).toFixed(2)}%
                  </Typography>
                )}
                <span>Current Share of Pool</span>
              </Stack>
            </>
          ) : (
            <>
              <Stack gap={0.25}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
              <Stack gap={0.25}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
              <Stack gap={0.25}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
            </>
          )}
        </Box>
        <Dialog
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
          position="top"
          sx={{
            "& .MuiDialog-paper": {
              minHeight: 350,
              justifyContent: "center",
            },
          }}
          open={confirmDialogOpen}
          onClose={closePopover}
        >
          <IconButton
            onClick={closePopover}
            size="large"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 1,
            }}
          >
            <XClose />
          </IconButton>
          {orderStatus === "pending" ? (
            <RemoveOrderPendingForm
              lpAmountNormalized={lpAmountNormalized}
              expectedTokenA={orderExpectedTokenA}
              expectedTokenB={orderExpectedTokenB}
              lpToken={lpToken as TokenInfo}
              tokenA={tokenA as TokenInfo}
              tokenB={tokenB as TokenInfo}
            />
          ) : orderStatus === "success" ? (
            <RemoveOrderSuccessForm
              lpAmountNormalized={lpAmountNormalized}
              tokenAOutput={tokenAOutput as bigint}
              tokenBOutput={tokenBOutput as bigint}
              lpToken={lpToken as TokenInfo}
              tokenA={tokenA as TokenInfo}
              tokenB={tokenB as TokenInfo}
              poolId={poolId}
              orderConfirmationId={orderConfirmationId as string}
            />
          ) : (
            <Stack gap={2}>
              <RemoveOrderPreviewForm
                lpToken={lpToken as TokenInfo}
                lpAmount={lpAmount}
                tokenA={tokenA as TokenInfo}
                tokenB={tokenB as TokenInfo}
              />
              <div>
                <RemoveOrderDetails
                  tokenA={tokenA}
                  tokenB={tokenB}
                  expectedTokenA={expectedTokenA}
                  expectedTokenB={expectedTokenB}
                  lpAmountNormalized={lpAmountNormalized}
                />
              </div>
              {orderError && (
                <Alert type="error" topSpace>
                  {orderError}
                </Alert>
              )}
              {!orderStatus && (
                <ContinueButton onClick={doBurn} className="accent">
                  Confirm Withdrawal
                </ContinueButton>
              )}
              {orderStatus === "pendingWalletConfirmation" && (
                <ContinueButton disabled className="secondary">
                  Confirm Transaction in your Wallet
                </ContinueButton>
              )}
              {orderStatus === "error" && (
                <ContinueButton onClick={closePopover} className="secondary">
                  Close
                </ContinueButton>
              )}
            </Stack>
          )}
        </Dialog>
        {!activeAddr ? (
          <ContinueButton onClick={connect} className="accent">
            Connect Wallet
            <CardholderIcon />
          </ContinueButton>
        ) : (
          <ContinueButton
            onClick={showPopover}
            disabled={!!validationError}
            className={!validationError ? "accent" : "secondary"}
          >
            {validationError || "Withdraw Liquidity"}
          </ContinueButton>
        )}
      </Stack>
      {lpAmountNormalized !== 0n && (
        <>
          <Toolbar
            className={css`
              margin-top: calc(var(--spacing) * 1.25);
            `}
          >
            {newPoolShare === null ? (
              <Skeleton width={160} />
            ) : (
              <PriceLabel>
                {(Number(newPoolShare) / 100).toFixed(2)}% Your New Total Share of Pool
              </PriceLabel>
            )}
            <div />
            <IconButton
              size="medium"
              onClick={() => {
                setDetailsDropdownOpen((prev) => !prev)
              }}
              style={{
                transform: detailsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ChevronDown />
            </IconButton>
          </Toolbar>
          <Details>
            <Collapse in={detailsDropdownOpen}>
              <RemoveOrderDetails
                tokenA={tokenA}
                tokenB={tokenB}
                expectedTokenA={expectedTokenA}
                expectedTokenB={expectedTokenB}
                lpAmountNormalized={lpAmountNormalized}
              />
            </Collapse>
          </Details>
        </>
      )}
    </Page>
  )
}

const Toolbar = styled.div`
  width: 100%;
  padding: 0 calc(var(--spacing));
  box-sizing: border-box;

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  justify-items: center;

  & > *:nth-child(1) {
    justify-self: start;
  }
  & > *:nth-child(3) {
    justify-self: end;
  }
`

const Page = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Inputs = styled.section`
  background-color: var(--mui-palette-background-default);
  border-radius: var(--mui-shape-borderRadius);
`

const ContinueButton = styled(Button)`
  padding: 1.4rem 1.55rem;
  width: 100%;
  font-size: 1.35rem;
`
const PriceLabel = styled(Button)`
  padding: 0;
  font-weight: 700;
  text-align: start;
`

const Details = styled(Stack)`
  gap: 0;
  width: 100%;
  padding: 0 calc(var(--spacing) * 1.25);
  box-sizing: border-box;
`
